import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { label: "Philosophy", href: "#philosophy" },
  { label: "Treatments", href: "#treatments" },
  { label: "Clinic", href: "#gallery" },
  { label: "Branches", href: "#branches" },
  { label: "Visit", href: "#visit" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.15, duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
      data-testid="site-nav"
      style={{
        WebkitBackdropFilter: "blur(3px) saturate(1.8) brightness(1.03)",
        backdropFilter: "blur(3px) saturate(1.8) brightness(1.03)",
        background: "transparent",
      }}
      className={`fixed inset-x-0 top-0 z-40 transition-shadow duration-500 ${
        scrolled
          ? "shadow-[inset_0_1px_0_rgba(255,255,255,0.7),inset_0_-1px_0_rgba(255,255,255,0.18),0_10px_30px_-10px_rgba(10,30,54,0.10)]"
          : "shadow-[inset_0_1px_0_rgba(255,255,255,0.5)]"
      }`}
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-16 flex items-center justify-between h-20">
        <a href="#top" className="flex items-center gap-4" data-testid="logo">
          <img
            src="https://customer-assets.emergentagent.com/job_root-canal-experts-2/artifacts/bh33ynzv_IMG_0281.jpeg"
            alt="Apex Dental Care logo"
            className="h-12 md:h-14 w-auto object-contain rounded-sm shadow-sm"
          />
          <span
            className="font-brand text-[22px] md:text-[26px] leading-none text-ink uppercase tracking-[0.16em]"
            style={{ fontWeight: 800 }}
          >
            Apex Dental Care
          </span>
          <span className="eyebrow hidden xl:inline">Edamuttam · Kerala</span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-[13px] tracking-[0.18em] uppercase text-ink/80 hover:text-ink link-underline transition-colors duration-300"
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:block">
          <a
            href="tel:+919961717181"
            data-testid="nav-book-cta"
            className="group inline-flex items-center gap-3 bg-ink text-porcelain px-6 py-3 text-[12px] tracking-[0.22em] uppercase hover:bg-ink/90 transition-colors duration-500"
          >
            <span>Call to Book</span>
            <span aria-hidden className="inline-block transition-transform duration-500 group-hover:translate-x-1">→</span>
          </a>
        </div>

        <button
          className="md:hidden text-ink"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
            style={{
              WebkitBackdropFilter: "blur(3px) saturate(1.8) brightness(1.03)",
              backdropFilter: "blur(3px) saturate(1.8) brightness(1.03)",
              background: "transparent",
            }}
            className="md:hidden overflow-hidden shadow-[inset_0_1px_0_rgba(255,255,255,0.5),0_10px_30px_-10px_rgba(10,30,54,0.10)]"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-sm tracking-[0.2em] uppercase text-ink"
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </a>
              ))}
              <a
                href="tel:+919961717181"
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex items-center justify-between bg-ink text-porcelain px-5 py-3 text-[12px] tracking-[0.22em] uppercase"
                data-testid="mobile-nav-book-cta"
              >
                <span>Call to Book</span>
                <span>→</span>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
